/**
 * Окно «Создать проект» по видео-референсу (спека §12, §10.2): платье (окно выбора, одно), стилевая
 * библиотека (умолчание — `default_library_id` из `/api/video/options`), имя, потолок трат, сводка кадров и
 * длительностей. ПЛАТНО (композер): ключ идемпотентности держит `useCreateProject` до успеха.
 *
 * Ответ 200 с `error` — проект уже создан, сценария нет: окно остаётся, форма замирает на отправленном
 * наборе, «Повторить» уходит с ТЕМ ЖЕ ключом (новый набор — новый ключ — второй проект). Заморозка липкая
 * (`projectFreezeStep`): её ставит и исход «проект мог создаться» (409 `BUSY`, 5xx, обрыв), а снимают только
 * применённый сценарий и закрытие окна — повтор, получивший 409, форму не размораживает.
 */
import { useQuery } from "@tanstack/react-query";
import { Shirt } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";

import { DressPickerDialog } from "@/components/fitting/DressPickerDialog";
import { Btn, ErrorLine, Hint, inputClass, selectClass } from "@/components/project/ui";
import { Dialog, DialogFooter } from "@/components/trends/Dialog";
import { mediaUrl, num } from "@/lib/media";
import { loadPhotos, photoKeys } from "@/lib/photos";
import { errorText } from "@/lib/project";
import { useVideoOptions } from "@/lib/video";
import {
  planLabel,
  projectFreezeStep,
  projectProblems,
  type VideoRefLimits,
  type VideoRefScene,
} from "@/lib/videoRef";
import { useCreateProject, type ProjectBody } from "@/lib/videoRefApi";
import { parseCap } from "@/lib/videoRun";

const DEFAULT_CAP = "7.00";
const NO_IDS: readonly number[] = [];
const NO_NOTES: ReadonlyMap<number, string> = new Map();

export function CreateProjectDialog({
  open,
  onClose,
  refId,
  scenes,
  limits,
  estimate,
  beforeSend,
}: {
  open: boolean;
  onClose: () => void;
  refId: number;
  scenes: VideoRefScene[];
  limits: VideoRefLimits;
  /** Оценка композера, $. */
  estimate: number;
  /** Перед платным вызовом: дождаться сохранения комментариев (они уходят композеру). */
  beforeSend: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const create = useCreateProject(refId);
  const options = useVideoOptions();
  const [picker, setPicker] = useState(false);
  const [dressId, setDressId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [libraryId, setLibraryId] = useState<number | null>(null);
  const [capText, setCapText] = useState(DEFAULT_CAP);
  const [sent, setSent] = useState<ProjectBody | null>(null);
  const [prepError, setPrepError] = useState("");
  const [stuck, setStuck] = useState(false);

  const dresses = useQuery({ queryKey: photoKeys.list, queryFn: loadPhotos, enabled: picker || dressId !== null });
  const dress = dresses.data?.dresses.find((d) => d.id === dressId) ?? null;
  const libraries = options.data?.libraries ?? [];
  const fallback =
    options.data && libraries.some((l) => l.id === options.data.default_library_id) ? options.data.default_library_id : null;
  const library = libraryId ?? fallback;
  const cap = parseCap(capText);

  const result = create.data;
  // Проект есть или мог создаться — форма замирает на отправленном наборе до применённого сценария или
  // закрытия окна; повтор — тем же ключом. Отказ гейта (прочий 4xx) проекта не создаёт и заморозку не трогает.
  useEffect(() => {
    const step = projectFreezeStep(create.data, create.failure);
    if (step !== "keep") setStuck(step === "freeze");
  }, [create.data, create.failure]);
  const frozen = sent !== null && stuck;
  const problems = projectProblems(scenes, limits);
  const ready = dressId !== null && library !== null && cap !== undefined && problems.length === 0;

  const pick = (ids: number[]) => {
    const first = ids[0];
    if (first === undefined) return;
    const card = dresses.data?.dresses.find((d) => d.id === first);
    setDressId(first);
    setName(`${card?.name ?? `Платье ${first}`} · по референсу ${refId}`);
  };

  const send = async (body: ProjectBody) => {
    if (create.busy) return;
    setPrepError("");
    try {
      await beforeSend();
    } catch (e) {
      setPrepError(`комментарии не сохранились: ${errorText(e)}`);
      return;
    }
    setSent(body);
    create.run(body);
  };

  const submit = () => {
    if (frozen && sent) return void send(sent);
    if (!ready || dressId === null) return;
    void send({ dress_id: dressId, library_id: library, name: name.trim() || null, cost_cap: cap ?? null });
  };

  // Закрытие окна — новый старт: заморозка и исход снимаются. Пока запрос в полёте — нет: его исход ещё
  // заморозит форму (ответ придёт в смонтированное окно).
  const close = () => {
    if (!create.busy) {
      setStuck(false);
      setSent(null);
      create.reset();
    }
    onClose();
  };

  // успех со сценарием — сразу в проект, на вкладку «Сценарий»
  useEffect(() => {
    if (result?.applied && result.error == null) void navigate(`/project/${result.project_id}?tab=scenario`);
  }, [result, navigate]);

  return (
    <Dialog open={open} onClose={close} title="Создать проект по референсу">
      <form
        className="grid gap-3 px-4 py-3"
        data-annot="видео-референс: окно создать проект"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="flex flex-wrap items-center gap-2">
          <Btn onClick={() => setPicker(true)} disabled={frozen}>
            <Shirt size={13} />
            {dressId === null ? "Выбрать платье" : "Другое платье"}
          </Btn>
          {dressId !== null ? (
            <span className="flex items-center gap-2 text-[12.5px] text-fg">
              {dress?.cover?.thumb_path ? (
                <img
                  src={mediaUrl(dress.cover.thumb_path)}
                  alt=""
                  className="h-[42px] w-[28px] rounded-[3px] border border-border object-cover"
                />
              ) : null}
              {dress?.name ?? `Платье #${dressId}`}
              {dress ? <span className="text-fg-faint">· фото: {num(dress.count)}</span> : null}
            </span>
          ) : null}
        </div>

        <select
          value={library ?? ""}
          disabled={frozen}
          onChange={(e) => setLibraryId(e.target.value ? Number(e.target.value) : null)}
          aria-label="стилевая библиотека"
          className={selectClass}
        >
          {library === null ? <option value="">стилевая библиотека…</option> : null}
          {libraries.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name} · {num(l.photos)}
            </option>
          ))}
        </select>

        <input
          value={name}
          disabled={frozen}
          onChange={(e) => setName(e.target.value)}
          placeholder="название проекта"
          aria-label="название проекта"
          className={inputClass}
        />

        <label className="flex items-center gap-1.5 text-[12.5px] text-fg-muted">
          потолок трат, $
          <input
            value={capText}
            disabled={frozen}
            onChange={(e) => setCapText(e.target.value)}
            inputMode="decimal"
            placeholder="без потолка"
            aria-label="потолок трат проекта, $"
            className={`${inputClass} tnum w-24 ${cap === undefined ? "border-err" : ""}`}
          />
          {cap === undefined ? <span className="text-[11.5px] text-err">число ≥ 0 или пусто</span> : null}
        </label>

        <Hint>{planLabel(scenes, estimate)}</Hint>
        {problems.length > 0 ? <ErrorLine text={problems.join("; ")} /> : null}
        <ErrorLine text={prepError || create.error || (options.isError ? `Библиотеки не загрузились: ${errorText(options.error)}` : "")} />
        {result?.error ? (
          <div className="grid gap-1.5">
            <ErrorLine text={`Проект ${result.project_id} создан, сценарий не составлен: ${result.error.message}`} />
            <Link to={`/project/${result.project_id}?tab=scenario`} className="text-[12.5px] text-accent-text hover:underline">
              открыть проект ↗
            </Link>
          </div>
        ) : null}
      </form>

      <DialogFooter>
        <Btn tone="ghost" onClick={close}>
          Отмена
        </Btn>
        <Btn
          tone="primary"
          disabled={create.busy || (!frozen && !ready)}
          onClick={submit}
          title="платно: композер составит сценарий"
        >
          {create.busy ? "Составляю…" : frozen ? "Повторить" : "Создать"} ≈ ${estimate.toFixed(2)}
        </Btn>
      </DialogFooter>

      <DressPickerDialog
        open={picker}
        onClose={() => setPicker(false)}
        list={dresses.data}
        taken={NO_IDS}
        annotate={NO_NOTES}
        triedAll={null}
        single
        onAdd={pick}
      />
    </Dialog>
  );
}
